import React, { useLayoutEffect, useState } from 'react';
import styled from '@emotion/styled';
import { BsFillCameraVideoFill } from '@react-icons/all-files/bs/BsFillCameraVideoFill';
import { compareAsc } from 'date-fns';

import { useDynamicLottiePlayer } from '@/hooks/useDynamicLottiePlayer';

import { Duration } from '../../components/Duration';
import { EditorPlaceholder } from '../../components/EditorPlaceholder';
import { useEditorNode } from '../../hooks';
import { DocumentPath } from '../../types';

export interface LottieProps {
  src?: string;
  thumbnail?: string;
  duration?: Duration;
  path: DocumentPath;
}

export function Lottie({
  src,
  thumbnail,
  duration,
  path,
}: LottieProps) {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [onDuration, setOnDuration] = useState(false);
  const { displayMode, displayDate } = useEditorNode(path);
  const LottiePlayer = useDynamicLottiePlayer();

  useLayoutEffect(() => {
    if (!duration) { return setOnDuration(true); }
    let interval: number;

    const compareDisplayDateToDuration = () => {
      const currentDate = displayDate ?? new Date();

      if (duration.startAt) {
        const beforeStart = compareAsc(currentDate, new Date(duration.startAt)) < 0;
        if (beforeStart) {
          return setOnDuration(false);
        }
      }
      if (duration.endAt) {
        const beforeEnd = compareAsc(currentDate, new Date(duration.endAt)) < 0;
        if (beforeEnd) {
          return setOnDuration(true);
        }
        setOnDuration(false);
        clearInterval(interval);
        return;
      }

      setOnDuration(true);
      clearInterval(interval);
    };

    compareDisplayDateToDuration();
    interval = window.setInterval(compareDisplayDateToDuration, 1000);
    return () => clearInterval(interval);
  }, [duration?.startAt, duration?.endAt, displayDate]);

  if (!onDuration && displayMode !== 'edit') { return null; }

  if (!src) {
    return (
      <EditorPlaceholder icon={<BsFillCameraVideoFill />} message="파일을 등록해주세요." />
    );
  }

  return (
    <Container>
      { !isLoaded && thumbnail && (
        <Thumbnail src={thumbnail} />
      ) }
      { LottiePlayer && (
        <LottiePlayer
          src={src}
          autoplay
          loop
          onEvent={(event: string) => {
            if (event === 'play' && !isLoaded) {
              setIsLoaded(true);
            }
          }}
          style={{ fontSize: 0, width: '100%' }}
        />
      ) }
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  font-size: 0;
`;

const Thumbnail = styled.img`
  width: 100%;
`;
